import { ShipmentRoadFromTraccar } from './../models/shipment-road-from-traccar.model';
import { ShipmentPrices } from './../models/shipment-prices.model';
import { Inject, Injectable } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { Mapper } from '@core/services/mapper/base-mapper.mapper';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TrailerLabel } from '@features/shipments/enums/trailer-label.enum';
import { DetectionStatus } from '@shared-features/enums/detection-status.enum';
import { CustomerType } from '@shared/enums/customer-type.enum';
import { DriversByDetectionType } from '@shared-features/enums/drivers-by-detection-type.enum';
import { DriversType } from '@shared-features/enums/drivers-type.enum';
import { GeneralResponse } from '@shared/enums/general-response.enum';
import { Shipment } from '@shared-features/models/shipment';
import { Contract } from '@features/contracts/models/contract';
import { Tracking } from '@features/live-tracking/models/tracking-driver';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { DriverWithTruck } from '@features/fleets/models/driver-with-truck';
import { IGetAllShipmentsPayload } from '@shared-features/interfaces/shipments-payload.interface';
import { DOCUMENT } from '@angular/common';
import { ApiConstant } from '@shared/constants/api.constant';
import { ShipmentRequest } from '@features/shipments/models/shipment-request.model';
import { AVLSummary } from '@features/live-tracking/models/avl-summary.model';
import { RCustomerLookupEntityType } from '@shared-features/enums/RCustomer-lookup-entity-type.enum';
import { ShipmentDateFilterType } from '@shared-features/enums/shipment-date-filter-type.enum';
import { ShipmentAssignedFleetsType } from '@shared-features/enums/shipment-assigned-fleets-type.enum';
import { DateRangeType } from '@shared/enums/date-range-type.enum';
import { Status } from '@shared/enums/status.enum';
import { AVLGeofencingUpdates } from '@features/live-tracking/models/avl-geofencing-updates.model';
import { WCCDeliveryNoteLine } from '@features/shipment-creation-template/models/wcc-delivery-note-line';
import { ShipmentsCount } from '@features/shipments/models/shipmentsCount.model';
import { Fleet } from '@features/fleets/models/fleet';
import { Driver } from '@features/fleets/models/driver';
import { ShipmentsModel } from '../models/shipments.model';
import { ShipmentStatusCount } from '../models/shipment-status-count';
import { DoorHistory } from '../models/door-history.model';
import { ExportedFile } from '@shared-features/models/exported-file';
import { AssignFleetPayload } from '@features/shipments/models/assign-fleet-payload.model';
import { TemperatureAndHumidity } from '../models/temperature-and-humidity.model';
import { TransactionHistory } from '../models/transaction-history.model';
import { ShipmentStatus } from '../../shared-features/enums/shipment-status.enum';
import { SamInitialAssignment } from '../models/sam-truck.model';
import { ALL_OPTION_VALUE } from '@shared/components/forms';
import { CompanyLabelType } from '@shared/enums/company-label-type.enum';
import { ShipmentOrderRoute } from '../models/shipment-order-route.model';
import { ShipmentDeliveryNote } from '@features/shipments/models/shipment-delivery-note';
import { shipmentReceiverFleets } from '@features/fleets/models/shipment-receiver-fleets.model';
import { EnvironmentTypes } from '@shared/enums/environment-types.enum';
import { ShipmentDto } from '../dto/shipment.dto';
import { TrackingType } from '@shared/enums/tracking-type.enum';
import { ShipmentDeliveryNoteDto } from '../dto/shipment-delivery-note.dto';
import { BidsData } from '@features/shipments/models/bids.model';
import { DispatchEvent } from '@features/shipments/models/dispatch-activity.model';
import { DeliveryNoteVerifyStatus } from '../enums/delivery-note-verify-status.enum';
import { WaitingChargeLogs } from '@features/shipments/models/waiting-charge-logs.model';

@Injectable({
  providedIn: 'root',
})
export class ShipmentsApiService {
  constructor(
    private baseAPI: ApiBaseService,
    private mapper: Mapper,
    @Inject(DOCUMENT) private document: Document
  ) {}

  // overload assignFleet
  assignFleet(payload: AssignFleetPayload): Observable<GeneralResponse> {
    return this.baseAPI
      .post(
        ApiConstant.ASSIGN_FLEET,
        filterNullEntity({
          ...payload,
          assignType: payload.assignType || DriversType.Fleet,
        })
      )
      .pipe(map((res) => res.data));
  }

  assignFreelance(
    shipmentId: number,
    driverId: number,
    assignType: DriversType,
    customerPrice: number,
    fleetPrice: number,
    sendToCustomer?: boolean,
    sendToReceiver?: boolean,
    customerMobileNumber?: string,
    receiverMobileNumber?: string,
    reassignComment?: string
  ): Observable<GeneralResponse> {
    const body = filterNullEntity({
      driverId,
      shipmentId,
      assignType,
      customerPrice,
      fleetPrice,
      sendToCustomer,
      sendToReceiver,
      customerMobileNumber,
      receiverMobileNumber,
      reassignComment,
    });
    return this.baseAPI.post(ApiConstant.ASSIGN_FLEET, body).pipe(map((res) => res.data));
  }

  getAllShipments(data: IGetAllShipmentsPayload): Observable<ShipmentsModel> {
    const keys = ['customerId', 'fleetId', 'customerLabel', 'creator', 'assignedBy'];

    keys.forEach((key) => {
      data = Object.assign({}, data, {
        [key]: this.validateArray(data[key], key !== 'customerLabel'),
      });
    });

    return this.baseAPI
      .post(ApiConstant.GET_ALL_SHIPMENTS, filterNullEntity(data))
      .pipe(map((res) => this.mapper.fromJson(ShipmentsModel, res.data)));
  }

  getAllShipmentsCount(data: IGetAllShipmentsPayload): Observable<ShipmentsCount> {
    const keys = ['customerId', 'fleetId', 'customerLabel', 'creator', 'assignedBy'];

    keys.forEach((key) => {
      data = Object.assign({}, data, {
        [key]: this.validateArray(data[key], key !== 'customerLabel'),
      });
    });

    return this.baseAPI
      .post(ApiConstant.GET_ALL_SHIPMENTS_COUNT, filterNullEntity(data))
      .pipe(map((res) => this.mapper.fromJson(ShipmentsCount, res.data)));
  }

  getUserPerformance(): Observable<ShipmentStatusCount> {
    return this.baseAPI
      .get(ApiConstant.GET_USER_PERFORMANCE)
      .pipe(map((res) => this.mapper.fromJson(ShipmentStatusCount, res.data)));
  }

  addShipment(
    shipment: Shipment,
    numberOfRepeats: number = 0,
    requests: ShipmentRequest[],
    type: RCustomerLookupEntityType
  ): Observable<Shipment> {
    const body = filterNullEntity({
      customerId: shipment.customer?.id,
      childCustomerId: shipment.childCustomer?.id,
      productTypeId: shipment.productType?.id,
      truckTypeId: shipment.truckType?.id,
      optionalTruckTypeIds: shipment.optionalTruckTypes?.map((truckType) => (truckType.id)),
      receiverId: type === RCustomerLookupEntityType.Receiver ? shipment.receiver?.id : null,
      dropOffCustomerId: type === RCustomerLookupEntityType.Customer ? shipment.receiver?.id : null,
      pickUpLocationId: shipment.pickUpLocation?.id,
      dropOffLocationId: shipment.receiverLocation?.id,
      contactPersonId:
        type === RCustomerLookupEntityType.Receiver ? shipment.contactPerson?.id : null,
      pickUpDate: new Date(new Date(shipment.pickedDateTime)),
      deliveryDate: new Date(new Date(shipment.deliveryDate)),
      repeatedNumber: numberOfRepeats,
      volume: 0,
      name: shipment.name,
      notes: shipment.notes,
      weight: shipment.weight,
      isFreeLanced: shipment.isFreeLanced,
      customerPrice: shipment.customerSpotRate,
      fleetPrice: shipment.fleetSpotRate,
      driverId: shipment.driver?.id,
      priceComment: shipment.priceComment,
      quantity: shipment.quantity,
      weightMeasurementUnit: shipment.weightMeasurementUnit,
      requests: (requests || []).map((request) => ({
        fromType: request.fromType,
        toType: request.toType,
        toLocationId: request.toLocation.id,
        fromLocationId: request.fromLocation.id,
        trailer: request.trailer?.id,
        dropOffUserId: request.dropOffUser?.id,
      })),
      driverAllowance: shipment.driverAllowance,
      totalPrice: shipment.totalPrice,
      truckId: shipment.truck?.id,
      fleetId: shipment.fleet?.id,
      deliveryNoteReference: shipment.deliveryNoteReference,
      creationTemplateType: shipment.shipmentCreationTemplate,
      startShipment: shipment.startShipment,
      comment: shipment.comment,
      forceClose: shipment.forceClose,
      assignAndAutoStart: shipment.assignAndAutoStart,
      dynamicFields: shipment?.dynamicFields || [],
      isBroadcasted: shipment?.isBroadcasted,
      autoDispatching: shipment?.autoDispatching,
    });

    return this.baseAPI
      .post(ApiConstant.SHIPMENT_ADD, body)
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data) as Shipment));
  }

  updateShipment(shipmentDto: Partial<ShipmentDto>): Observable<Shipment> {
    return this.baseAPI
      .put(ApiConstant.SHIPMENT_UPDATE, shipmentDto)
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data) as Shipment));
  }

  updateShipmentPrice(body: {
    uuid: string;
    customerPrice: number;
    fleetPrice: number;
    priceComment: string;
  }): Observable<Shipment> {
    return this.baseAPI
      .put(ApiConstant.SHIPMENT_UPDATE, filterNullEntity(body))
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data) as Shipment));
  }

  getCustomerRate(
    customerId: number,
    truckTypeId: number,
    fromCityId: number,
    toCityId: number,
    productTypeId: number
  ): Observable<Contract> {
    const params = filterNullEntity({
      id: customerId,
      fromCityId,
      toCityId,
      truckTypeId,
      productTypeId,
    });

    return this.baseAPI
      .get(ApiConstant.GET_CUSTOMER_RATE, { params })
      .pipe(map((res) => this.mapper.fromJson(Contract, res.data)));
  }

  getSpotRates(
    id: number,
    fleetId: number,
    truckTypeId: number,
    fromCityId: number,
    toCityId: number,
    productTypeId?: number
  ): Observable<ShipmentPrices> {
    const params = filterNullEntity({
      id,
      fleetId,
      fromCityId,
      toCityId,
      truckTypeId,
      productTypeId,
    });
    return this.baseAPI.get(ApiConstant.API_GET_SPOT_PRICES_RATE, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentPrices, res.data);
      })
    );
  }

  updateShipmentStatus(body: {
    id: number;
    action: ShipmentStatus;
    comment?: string;
  }): Observable<Shipment> {
    return this.baseAPI
      .put(ApiConstant.SHIPMENT_UPDATE_STATUS, body)
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data)));
  }

  // live tracking
  getShipmentRoad(shipmentId: number): Observable<Tracking[]> {
    return this.baseAPI
      .get(ApiConstant.GET_SHIPMENT_PATH + shipmentId)
      .pipe(map((res) => this.mapper.fromJson(Tracking, res)));
  }

  getShipmentRoadFromTracker(
    shipmentId: number,
    trackDevice?: TrackingType
  ): Observable<ShipmentRoadFromTraccar> {
    return this.baseAPI
      .get(ApiConstant.GET_SHIPMENT_AVL_PATH + shipmentId, {
        params: filterNullEntity({ trackDevice }),
      })
      .pipe(map((res) => this.mapper.fromJson(ShipmentRoadFromTraccar, res.data)));
  }

  updateShipmentPriceAndDeliveryNote(shipment: ShipmentDeliveryNoteDto): Observable<Shipment> {
    return this.baseAPI
      .put(ApiConstant.SHIPMENT_UPDATE, filterNullEntity(shipment))
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data) as Shipment));
  }

  deleteShipment(shipmentId: number): Observable<string> {
    const body = {
      id: shipmentId,
    };
    return this.baseAPI.delete(`${ApiConstant.DELETE_SHIPMENT}${shipmentId}`, body);
  }

  resendOTP(shipmentId: number): Observable<string> {
    const body = { shipmentId };
    return this.baseAPI
      .put(ApiConstant.RESEND_OTP, body)
      .pipe(map((res) => this.mapper.fromJson(Shipment, res.data)));
  }

  addDriverWithTruck(driverWithTruck: DriverWithTruck): Observable<Driver> {
    const body = {
      addDriverDto: filterNullEntity({
        firstName: driverWithTruck.firstName,
        lastName: driverWithTruck.lastName,
        mobileNumber: driverWithTruck.mobileNumber,
        birthDate: driverWithTruck.birthDate,
        fleetId: driverWithTruck.fleetId,
        nonResidentId: driverWithTruck.nonResidentId,
        iqamaId: driverWithTruck.iqamaId,
        iqamaIdExpiry: driverWithTruck.iqamaIdExpiry,
        nationality: driverWithTruck.nationality,
        owner: driverWithTruck.driverOwner,
        licenseType: driverWithTruck.licenseType,
        licenseExpiry: driverWithTruck.driverLicenseExpiry,
      }),
      addTruckDto: filterNullEntity({
        fleetId: driverWithTruck.fleetId,
        plateNumber: driverWithTruck.plateNumber,
        sequenceNumber: driverWithTruck.sequenceNumber,
        manufactureYear: driverWithTruck.manufactureYear,
        truckTypeId: driverWithTruck.truckTypeId,
        ownerName: driverWithTruck.truckOwnerName,
        brandId: driverWithTruck.brandId,
        weight: driverWithTruck.weight,
        licenseExpiry: driverWithTruck.truckLicenseExpiry,
        ownerId: driverWithTruck.ownerId,
        plateNumberDetails: driverWithTruck.plateNumberDetails,
      }),
    };

    return this.baseAPI
      .post(ApiConstant.ADD_DRIVER_WITH_TRUCK, body)
      .pipe(map((res) => this.mapper.fromJson(Driver, res.data)));
  }

  getAllActiveFleets(): Observable<Fleet[]> {
    return this.baseAPI.get(ApiConstant.GET_ACTIVE_FLEETS).pipe(
      map((res) => {
        return this.mapper.fromJson(Fleet, res.data);
      })
    );
  }

  getShipmentTrackingCoordinatesFromUrl<T = Tracking>(url: string, model?): Observable<T[]> {
    const {
      location: { protocol, hostname, port },
    } = this.document;
    const currentHostUrl = `${protocol}//${hostname}${port ? ':' + port : ''}`;
    const options = { headers: this.baseAPI.getHeaders() };
    options.headers.append('Access-Control-Allow-Origin', currentHostUrl);
    options.headers.append('Access-Control-Allow-Credentials', 'true');
    options.headers.append('Origin', currentHostUrl);

    return this.baseAPI.get(url, options).pipe(
      tap((res) => {
        return res.data;
      }),
      map((response) => {
        return this.mapper.fromJson(model || Tracking, response.coordinates);
      })
    );
  }

  detectDriversLocations(body: {
    latitude: number;
    longitude: number;
    maximumDistanceInMeter: number;
    driverStatus: DetectionStatus;
    customerId?: number;
    fleetId?: number;
    'fleetTypes[]': DriversByDetectionType[];
  }): Observable<Tracking[]> {
    return this.baseAPI
      .get(ApiConstant.DETECT_DRIVERS, { params: filterNullEntity<typeof body>(body) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(Tracking, res);
        })
      );
  }

  markShipmentAsPaidForDriver(body: { shipmentId: number }): Observable<string> {
    return this.baseAPI.put(ApiConstant.MARK_SHIPMENT_AS_PAID_FOR_DRIVER, body).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res);
      })
    );
  }

  downloadEwaybill(shipmentId: number): Observable<ExportedFile> {
    return this.baseAPI.get(ApiConstant.DOWNLOAD_EWAYBILL + '/' + shipmentId).pipe(
      map((res) => {
        return this.mapper.fromJson(ExportedFile, res.data);
      })
    );
  }

  exportShipments(
    data: IGetAllShipmentsPayload & {
      isForExport?: boolean;
      exportedColumns;
    }
  ): Observable<ExportedFile> {
    const keys = ['customerId', 'fleetId', 'customerLabel', 'creator', 'assignedBy'];

    keys.forEach((key) => {
      data = Object.assign({}, data, {
        [key]: this.validateArray(data[key], key !== 'customerLabel'),
      });
    });

    return this.baseAPI.post(ApiConstant.API_EXPORT_SHIPMENTS, filterNullEntity(data)).pipe(
      map((res) => {
        return this.mapper.fromJson(ExportedFile, res.data);
      })
    );
  }

  getShipmentRequests(id: string): Observable<ShipmentRequest[]> {
    return this.baseAPI
      .get(ApiConstant.GET_SHIPMENT_REQUESTS + id)
      .pipe(map((res) => this.mapper.fromJson(ShipmentRequest, res.data)));
  }

  getAVLsummary(shipmentId: number): Observable<AVLSummary> {
    return this.baseAPI.get(ApiConstant.GET_SHIPMENT_AVL_SUMMARY + `${shipmentId}`).pipe(
      map((res) => {
        return this.mapper.fromJson(AVLSummary, res.data);
      })
    );
  }

  getShipmentsForRental(fromCityId: number): Observable<ShipmentsModel> {
    const params = filterNullEntity({
      fromCityId,
      shipmentFleetTypeFilter: ShipmentAssignedFleetsType.All,
      listForRental: true,
      status: Status.NEW,
      customerServiceType: CustomerType.Service,
      dateRangeType: DateRangeType.TODAY,
      dateField: ShipmentDateFilterType.pickUpDate,
    });

    return this.baseAPI.get(ApiConstant.GET_ALL_SHIPMENTS, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentsModel, res.data);
      })
    );
  }

  markShipmentAsReceived(shipmentId: number): any {
    return this.baseAPI.post(ApiConstant.MARK_SHIPMENT_DELIVERED + `${shipmentId}`, {}).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res.data);
      })
    );
  }

  updateTrailerLabel(body: { requestId: number; label: TrailerLabel }): Observable<string> {
    return this.baseAPI.put(ApiConstant.UPDATE_TRAILER_LABEL, body).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res);
      })
    );
  }

  getShipmentGeofencingUpdates(id: number): Observable<AVLGeofencingUpdates[]> {
    return this.baseAPI.get(ApiConstant.GET_GEOFENCING_SHIPMENT_UPDATES + `${id}`).pipe(
      map((res) => {
        return this.mapper.fromJson(AVLGeofencingUpdates, res.data);
      })
    );
  }

  getWCCDeliveryNoteLine(
    shipmentId: number,
    customerId: number
  ): Observable<WCCDeliveryNoteLine[]> {
    const params = filterNullEntity({
      shipmentId,
      customerId,
    });

    return this.baseAPI.get(ApiConstant.WCC_GET_DELIVERY_NOTE_LINE, { params }).pipe(
      map((res) => {
        return this.mapper.fromJson(WCCDeliveryNoteLine, res.data);
      })
    );
  }

  private validateArray(value: any[], isNumber: boolean): any[] {
    if (
      !value ||
      (Array.isArray(value) && !value.length) ||
      (typeof value !== 'number' ? value?.includes(ALL_OPTION_VALUE) : false)
    ) {
      return null;
    }
    return (Array.isArray(value) ? value : [value]).map((item) =>
      isNumber ? Number(item) : CompanyLabelType[item]
    );
  }

  getDoorOpenHistory(id: number): Observable<DoorHistory[]> {
    return this.baseAPI.get(ApiConstant.GET_SHIPMENT_DOOR_OPEN_HISTORY + '/' + id).pipe(
      map((res) => {
        return this.mapper.fromJson(DoorHistory, res.data);
      })
    );
  }

  getOrdersCustomers(shipmentId: number): Observable<String[]> {
    return this.baseAPI
      .get(ApiConstant.GET_ORDERS_CUSTOMERS + shipmentId)
      .pipe(map((res) => this.mapper.fromJson(String, res.data)));
  }
  getTemperatureAndHumidity(shipmentId: number): Observable<TemperatureAndHumidity> {
    return this.baseAPI.get(ApiConstant.GET_TEMPERATURE_AND_HUMIDITY + shipmentId).pipe(
      map((res) => {
        return this.mapper.fromJson(TemperatureAndHumidity, res.data);
      })
    );
  }

  getTransactionHistory(body: {
    shipmentId: number;
    type?: string;
  }): Observable<TransactionHistory[]> {
    return this.baseAPI
      .get(ApiConstant.GET_TRANSACTION_HISTORY, { params: filterNullEntity(body) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(TransactionHistory, res.data);
        })
      );
  }

  saveInitialAssign(Payload: { truckId: number; shipmentId: number }): Observable<string> {
    return this.baseAPI.post(ApiConstant.SAVE_INITIAL_ASSIGNMENT, Payload).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  updateExpectedDelivery(body: {
    shipmentId: number;
    expectedDeliveryDate?: Date;
  }): Observable<string> {
    const options = { params: body };
    return this.baseAPI.put(ApiConstant.UPDATE_EXPECTED_DELIVERY_DATE, body, options).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  getTruckForSam(body: {
    shipmentId: number;
    offset: number;
    limit: number;
  }): Observable<SamInitialAssignment> {
    const { shipmentId, ...payload } = body;
    return this.baseAPI
      .get(ApiConstant.GET_TRUCK_FOR_SAM + '/' + shipmentId, { params: filterNullEntity(payload) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(SamInitialAssignment, res.data);
        })
      );
  }

  getShipmentOrderRoute(id: number): Observable<ShipmentOrderRoute[]> {
    return this.baseAPI.get(ApiConstant.GET_SHIPMENT_ORDER_ROUTE + `/${id}`).pipe(
      map((res) => {
        return this.mapper.fromJson(ShipmentOrderRoute, res.data);
      })
    );
  }
  getDeliveryNotes(params: { shipmentId: number }): Observable<ShipmentDeliveryNote[]> {
    return this.baseAPI
      .get(ApiConstant.GET_DELIVERY_NOTE, { params: filterNullEntity<typeof params>(params) })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(ShipmentDeliveryNote, res.data);
        })
      );
  }

  updateDeliveryNoteVerificationStatus(body: {deliveryNoteId: number, status: DeliveryNoteVerifyStatus, comment: string}): Observable<ShipmentDeliveryNote> {
    return this.baseAPI
      .put(ApiConstant.UPDATE_DELIVERY_NOTE_VERIFICATION_STATUS, filterNullEntity(body))
      .pipe(
        map((res) => {
          return this.mapper.fromJson(ShipmentDeliveryNote, res.data);
        })
      );
  }


  receiveHardCopy(payload: {
    id: number;
    deliveryNoteId: number;
    isReceiveHardCopy: boolean;
  }): Observable<void> {
    return this.baseAPI.post(ApiConstant.CHECK_DN_HARD_COPY, filterNullEntity(payload)).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res.data);
      })
    );
  }

  getDistributionFleets(shipmentId: number): Observable<Fleet[]> {
    return this.baseAPI.get(ApiConstant.GET_DISTRIBUTE_FLEET + shipmentId).pipe(
      map((res) => {
        return this.mapper.fromJson(Fleet, res.data);
      })
    );
  }
  assignDistributedSelectedFleets(payload: {
    shipmentId: number;
    fleetIds: number[];
    envType: EnvironmentTypes;
  }) {
    return this.baseAPI
      .post(ApiConstant.DISTRIBUTE_SELECTED_FLEETS, filterNullEntity(payload))
      .pipe(
        map((res) => {
          return this.mapper.fromJson(Fleet, res.data);
        })
      );
  }

  getShipmentReceiverFleets(payload: {
    shipmentId: number;
    offset: number;
    limit: number;
    searchTerm?: string;
  }): Observable<shipmentReceiverFleets[]> {
    return this.baseAPI.post(ApiConstant.GET_FLEETS_LIST, filterNullEntity(payload)).pipe(
      map((res) => {
        return this.mapper.fromJson(shipmentReceiverFleets, res.data);
      })
    );
  }

  cancelBroadcast(shipmentId: number): Observable<string> {
    return this.baseAPI
      .put(`${ApiConstant.UPDATE_BROADCAST_STATUS}/${shipmentId}`, { isBroadcasted: false })
      .pipe(map((res) => this.mapper.fromJson(String, res.data)));
  }

  bulkAssignShipments(body: { shipmentUuids: string[]; fleetId: number }): Observable<string> {
    return this.baseAPI.post(ApiConstant.BULK_ASSIGN_SHIPMENTS, filterNullEntity(body)).pipe(
      map((res) => {
        return this.mapper.fromJson(String, res.data);
      })
    );
  }

  getShipmentBids(shipmentId: number): Observable<BidsData> {
    return this.baseAPI.get(ApiConstant.GET_SHIPMENT_BIDS + shipmentId).pipe(
      map((res) => this.mapper.fromJson(BidsData, res.data))
    );
  }

  getDispatchActivity(shipmentId: string): Observable<DispatchEvent[]> {
    return this.baseAPI.get(ApiConstant.GET_SHIPMENT_DISPATCH_ACTIVITY(shipmentId)).pipe(
      map((res) => this.mapper.fromJson(DispatchEvent, res.data))
    );
  }

  getWaitingChargeLogs(shipmentId: number): Observable<WaitingChargeLogs> {
    return this.baseAPI.get(ApiConstant.WAITING_CHARGE_LOGS(shipmentId)).pipe(
      map((res) => this.mapper.fromJson(WaitingChargeLogs, res.data)),
    );
  }

  updateWaitingChargeLogs(
    shipmentId: number, 
    paylod: { pickupWaitingTimeHours: number, dropoffWaitingTimeHours: number }
  ): Observable<WaitingChargeLogs> {
    return this.baseAPI.put(ApiConstant.WAITING_CHARGE_LOGS(shipmentId), paylod).pipe(
      map((res) => this.mapper.fromJson(WaitingChargeLogs, res.data)),
    );
  }

  approveWaitingChargeLogs(shipmentId: number): Observable<WaitingChargeLogs> {
    return this.baseAPI.post(ApiConstant.APPROVE_WAITING_CHARGE_LOGS(shipmentId), {}).pipe(
      map((res) => this.mapper.fromJson(WaitingChargeLogs, res.data)),
    );
  }

}
